<script setup lang="ts">
/**
 * The scope banner — the rung the ROUTE is showing, offered rather than applied (§6b): "you are
 * looking at X, scope the chat to it?" — the case on a case hub, the client on a client hub
 * (AlexAmin/marcus#69; the reasoning, including why a case wins when a route could name both, lives
 * in `useRouteScopeOffer`).
 *
 * **The open conversation no longer offers its own rung here** (D3, MAR-990). Its saved scope is
 * adopted outright and silently when it is opened (`adoptConversationScope`), so there is nothing
 * left for it to offer. The "this chat was scoped to X, restore it?" branch that f3d7b91f added is
 * gone with the behaviour it served.
 *
 * It renders ONLY while nothing is selected — `routeOffer` is null whenever `activeScope` is a case
 * or a client, so there is no state in which this contradicts the scope row it sits above.
 *
 * Accepting goes through `setScope`, the single writer: it remembers the rung for the next new chat
 * and reveals the context rail, because clicking here is a user choosing a scope.
 *
 * An offer whose name did not resolve is not shown. A banner reading "scope to ''" asks the user to
 * agree to something unnamed, which is exactly the confirmation nobody can give.
 */
import { Target, X } from 'lucide-vue-next'
import { scopeLabel } from '~~/shared/web-chat/scope'
import { useRouteScopeOffer } from '~/composables/web-chat/useRouteScopeOffer'

/** Mirrors `ChatScopeBar`: compact carries its own chrome, full sits inside the composer card. */
defineProps<{ compact?: boolean }>()

const { t } = useI18n()

const { routeOffer, acceptRouteOffer, dismissRouteOffer } = useRouteScopeOffer()

const offer = routeOffer
const label = computed(() => (offer.value ? scopeLabel(offer.value) : null))
const visible = computed(() => !!offer.value && !!label.value)
// The BODY is rung-neutral ("you're looking at {scope}") and the label inside it already says which
// rung it is; only the BUTTON has to name one, because "Acotar a este caso" on a client hub is a
// button that promises the wrong rung.
const acceptKey = computed(() => (offer.value?.kind === 'client'
  ? 'clients.hub.scopeRouteOfferAcceptClient'
  : 'clients.hub.scopeRouteOfferAccept'))

function accept() {
  void acceptRouteOffer()
}

function dismiss() {
  dismissRouteOffer()
}
</script>

<template>
  <Transition
    enter-active-class="transition-opacity duration-150 ease-out"
    enter-from-class="opacity-0"
    leave-active-class="transition-opacity duration-100 ease-in"
    leave-to-class="opacity-0"
  >
    <div
      v-if="visible"
      class="flex items-start gap-2 rounded-lg border border-accent-ink/25 bg-accent-lightest px-3 py-2"
      :class="compact ? 'mb-1.5' : 'mx-3 mb-1 mt-1.5'"
    >
      <div class="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent-surface">
        <Target class="h-3 w-3 text-accent-ink" />
      </div>

      <div class="min-w-0 flex-1">
        <!-- i18n-t so the scope name can be emphasised inside the sentence without splitting it into
             fragments translators cannot reorder (same reason as ClearScopeCard). -->
        <i18n-t
          keypath="clients.hub.scopeRouteOfferBody"
          tag="p"
          class="text-xs leading-relaxed text-muted-foreground"
          scope="global"
        >
          <template #scope><span class="font-medium text-foreground">{{ label }}</span></template>
        </i18n-t>

        <div class="mt-1.5 flex flex-wrap items-center gap-1.5">
          <button
            type="button"
            class="inline-flex items-center gap-1.5 rounded-md border border-accent-ink bg-accent-light px-2 py-1 text-xs font-medium text-accent-light-foreground transition-colors hover:bg-accent-lighter"
            @click="accept"
          >
            <Target class="h-3 w-3 shrink-0" />
            {{ t(acceptKey) }}
          </button>
          <button
            type="button"
            class="inline-flex items-center gap-1.5 rounded-md border border-border px-2 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent-lighter hover:text-foreground"
            @click="dismiss"
          >
            <X class="h-3 w-3 shrink-0" />
            {{ t('clients.hub.scopeOfferDismiss') }}
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>
